import styled from "@emotion/styled";
import { Box, Stack, Tab, Tabs, useTheme } from "@mui/material";
import { useEffect } from "react";

import type {
  JobDetailClipListItemDto,
  JobStatus,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { FinalizeTabContent } from "Projects/Components/FinalizeTabContent";
import { GeneralSettingsTabContent } from "Projects/Components/GeneralSettingsTabContent";
import { ParametersTabContent } from "Projects/Components/ParametersTabContent";
import { useScriptEditor } from "Projects/Components/ScriptEditor/ScriptEditorContext";

type Props = {
  projectID: string;
  jobID: string;
  jobStatus: JobStatus;
  clips: JobDetailClipListItemDto[];
  activeTabID: number;
  onTabChange: (tabID: number) => void;
};

export const TAB_IDS = {
  GeneralSettings: 0,
  Parameters: 1,
  Finalize: 2,
} as const;

const StyledTabs = styled(Tabs)`
  border-bottom: 1px solid ${(props) => props.theme.colors.border};

  .MuiTabs-indicator {
    background-color: ${(props) => props.theme.palette.primary.main};
    height: 3px;
  }

  .MuiTabs-flexContainer {
    height: 100%;
  }

  .MuiTabs-scroller {
    overflow: visible !important;
  }
`;

const StyledTab = styled(Tab)`
  flex: 1;
  max-width: none;
  min-height: 48px;
  text-transform: none;
  font-weight: 400;
  border-right: 1px solid ${(props) => props.theme.colors.border};

  &:last-of-type {
    border-right: none;
  }

  &.Mui-selected {
    color: ${(props) => props.theme.palette.primary.main};
  }
`;

export const JobDetailSidebar = ({
  projectID,
  jobID,
  jobStatus,
  clips,
  activeTabID,
  onTabChange,
}: Props): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  // Auto-switch to "Nastavení segmentu" tab whenever a segment receives focus.
  // Listening on `selectionNonce` (not just `selectedSegmentIndex`) means the
  // tab also switches when the same segment is re-focused (e.g. after the user
  // clicked outside the editor and came back) where the index wouldn't change.
  const { selectedSegmentIndex, selectionNonce } = useScriptEditor();
  useEffect(() => {
    // `selectionNonce` is a trigger only — reading it here silences the
    // noUnusedVariables lint and keeps the dependency array honest.
    void selectionNonce;
    if (selectedSegmentIndex !== null) {
      onTabChange(TAB_IDS.Parameters);
    }
  }, [selectedSegmentIndex, selectionNonce, onTabChange]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    onTabChange(newValue);
  };

  return (
    <div>
      <StyledTabs
        value={activeTabID}
        onChange={handleTabChange}
        variant="fullWidth"
      >
        <StyledTab label={t(Resources.JobDetail.GeneralSettingsTab)} />
        <StyledTab label={t(Resources.JobDetail.SegmentSettingsTab)} />
        <StyledTab label={t(Resources.JobDetail.FinalizeTab)} />
      </StyledTabs>

      <Box sx={{ padding: theme.spacing(Spacing.M) }}>
        <Stack spacing={Spacing.M}>
          {activeTabID === TAB_IDS.GeneralSettings && (
            <GeneralSettingsTabContent projectID={projectID} />
          )}
          {activeTabID === TAB_IDS.Parameters && (
            <ParametersTabContent projectID={projectID} />
          )}
          {activeTabID === TAB_IDS.Finalize && (
            <FinalizeTabContent
              projectID={projectID}
              jobID={jobID}
              jobStatus={jobStatus}
              clips={clips}
            />
          )}
        </Stack>
      </Box>
    </div>
  );
};
